import { useEffect, useState } from "react";

import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetExercise } from "api/hooks/useGetExercise";
import { useGetLesson } from "api/services/content/hooks/useGetLesson";
import { useProfileContext } from "contexts/ProfileContext";
import { ExercisePreview } from "routes/library/views/Exercises/components";
import { LessonPreview } from "routes/library/views/Lessons/components";
import { MUIUnifiedModal } from "shared/atoms/MUIUnifiedModal";
import { SkeletonLayout } from "shared/atoms/SkeletonLayout";
import { DARK_BLUE } from "theme/colors";

export type PreviewActivity = {
  id: number;
  part: number;
  type: "exercise" | "lesson";
};

interface ActivityPreviewModalProps {
  activity: PreviewActivity;
  onClose: () => void;
}

export const ActivityPreviewModal: React.FC<ActivityPreviewModalProps> = ({ activity, onClose }) => {
  const { profile } = useProfileContext();
  const [isVisible, setIsVisible] = useState(false);

  invariant(profile, "Profile should be defined");

  const { data: exerciseData, isLoading: exerciseLoading } = useGetExercise(activity.id, profile.locale as string, {
    enabled: activity.type === "exercise" && !!activity.id,
  });
  const { data: lessonData, isLoading: lessonLoading } = useGetLesson(activity.id, {
    enabled: activity.type === "lesson" && !!activity.id,
  });

  useEffect(() => {
    // Trigger animation after mount
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 200); // Wait for animation
  };

  const isLoading = exerciseLoading || lessonLoading;

  if (!exerciseData && !lessonData && !isLoading) {
    return null;
  }

  return (
    <MUIUnifiedModal open={isVisible} onClose={handleClose} width="1000px">
      <Container>
        {isLoading && (
          <SkeletonLayout
            layout={{
              type: "column",
              spacing: 3,
              padding: 4,
              children: [
                { type: "skeleton", variant: "text", typographyVariant: "h3", width: "40%" },
                {
                  type: "row",
                  spacing: 3,
                  children: [
                    {
                      type: "skeleton",
                      variant: "rounded",
                      height: 365,
                      sx: { minWidth: 300, maxWidth: 650, flex: 2 },
                    },
                    {
                      type: "column",
                      sx: { minWidth: 300, flex: 1 },
                      children: [
                        {
                          type: "column",
                          spacing: 1,
                          sx: { padding: "16px 0", borderBottom: `1px solid ${DARK_BLUE[30]}` },
                          children: [
                            { type: "skeleton", variant: "text", typographyVariant: "h4", width: "50%" },
                            { type: "skeleton", variant: "text", typographyVariant: "body_thin", width: "35%" },
                          ],
                        },
                        {
                          type: "column",
                          spacing: 1,
                          sx: { padding: "16px 0", borderBottom: `1px solid ${DARK_BLUE[30]}` },
                          children: [
                            { type: "skeleton", variant: "text", typographyVariant: "h4", width: "35%" },
                            { type: "skeleton", variant: "rounded", height: 26, width: "20%" },
                          ],
                        },
                        {
                          type: "column",
                          spacing: 1,
                          sx: { padding: "16px 0", borderBottom: `1px solid ${DARK_BLUE[30]}` },
                          children: [
                            { type: "skeleton", variant: "text", typographyVariant: "h4", width: "45%" },
                            { type: "skeleton", variant: "text", typographyVariant: "body_thin", lines: 5 },
                          ],
                        },
                        {
                          type: "column",
                          spacing: 1,
                          sx: { padding: "16px 0" },
                          children: [
                            { type: "skeleton", variant: "text", typographyVariant: "h4", width: "55%" },
                            { type: "skeleton", variant: "text", typographyVariant: "body_thin", lines: 3 },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            }}
          />
        )}

        {!isLoading && activity.type === "exercise" && exerciseData && (
          <ExercisePreview exercise={exerciseData} defaultLevel={activity.part} boxShadow={false} />
        )}

        {!isLoading && activity.type === "lesson" && lessonData && (
          <LessonPreview lesson={lessonData} defaultPart={activity.part} boxShadow={false} />
        )}
      </Container>
    </MUIUnifiedModal>
  );
};

const Container = styled.div`
  overflow-y: auto;
`;
